/**
 * The rule tree behind dynamic lists and playlist generation.
 *
 * design/README.md §10: *"rules are the content; the chrome nearly disappears."* Nested AND/OR/NOT
 * indented by left rules rather than boxed. §J15 then makes this the same object the playlist
 * generator reads, so there is one rule model in the app and not two that drift.
 *
 * Deliberately a small closed vocabulary rather than a query language. Every field here is a
 * column the library already has; nothing is computed at rule time. A rule builder that can
 * express something the database cannot answer is a builder that shows you an error instead of a
 * count, and the count updating as you type is the entire feel of this screen.
 */
/**
 * Every field a rule can be about, as a value rather than only a type.
 *
 * The API validates an incoming tree against this list. It used to repeat the names in a Zod
 * enum, which is two lists of the same thing — and the second one was silently missing `plays`
 * the day it was added, so every rule over the listening history came back a bad request.
 */
export const RULE_FIELDS = [
    "score",
    "year",
    "genre",
    "artist",
    "album",
    "title",
    "favourite",
    "rated",
    "ratedAt",
    "addedAt",
    /** From the listening history. Absent history means every song has 0 plays, not no answer. */
    "plays",
    "playedAt",
];
export const RULE_OPS = ["is", "is-not", "at-least", "at-most", "contains", "before", "after"];
export const EMPTY_GROUP = { kind: "group", join: "and", children: [] };
/** Which operators make sense for a field. The builder offers only these. */
export const FIELD_OPS = {
    score: ["is", "is-not", "at-least", "at-most"],
    year: ["is", "is-not", "at-least", "at-most"],
    genre: ["is", "is-not"],
    artist: ["is", "is-not", "contains"],
    album: ["is", "is-not", "contains"],
    title: ["contains", "is"],
    favourite: ["is"],
    rated: ["is"],
    ratedAt: ["before", "after"],
    addedAt: ["before", "after"],
    plays: ["is", "is-not", "at-least", "at-most"],
    playedAt: ["before", "after"],
};
/**
 * Fields whose value is a number, so the builder stores a number rather than the string a text
 * input hands it. `Plays at least "40"` compiles to a comparison against text and quietly sorts
 * lexicographically — 9 above 40.
 */
export const NUMERIC_FIELDS = ["score", "year", "plays"];
export const FIELD_LABELS = {
    score: "Score",
    year: "Year",
    genre: "Genre",
    artist: "Artist",
    album: "Album",
    title: "Title",
    favourite: "Favourite",
    rated: "Rated",
    ratedAt: "Rated",
    addedAt: "Added",
    plays: "Plays",
    playedAt: "Played",
};
const OP_LABELS = {
    is: "is",
    "is-not": "is not",
    "at-least": "is at least",
    "at-most": "is at most",
    contains: "contains",
    before: "before",
    after: "after",
};
/**
 * The operator word shown at the head of a rule row: `WHERE`, `AND`, `AND NOT`, `OR`.
 *
 * The first row in a group says WHERE; the rest say how they join. A group that negates says it
 * once, on its first row, rather than repeating NOT down the list — the design puts NOT on the
 * group, and a NOT on every row would read as a different tree.
 */
export function joinWord(group, index) {
    if (index === 0)
        return group.negate ? "WHERE NOT" : "WHERE";
    if (group.join === "or")
        return "OR";
    return group.negate ? "AND NOT" : "AND";
}
/**
 * The word above a NESTED group, which is a different question from the one `joinWord` answers.
 *
 * A rule row's word comes from the group it sits in. A nested group's word is how it JOINS its
 * parent plus its own negation — the NOT belongs to the child, not to the parent. Reusing
 * `joinWord(parent, index)` renders `+ NOT group` as a plain `AND`, which is a different tree
 * drawn convincingly.
 */
export function nestedJoinWord(parent, index, child) {
    const base = index === 0 ? "WHERE" : parent.join === "or" ? "OR" : "AND";
    return child.negate ? `${base} NOT` : base;
}
/** `Score is at least 8`. Used in the row and in the list index's one-line summary. */
export function describeRule(rule) {
    const field = FIELD_LABELS[rule.field];
    const op = OP_LABELS[rule.op];
    if (rule.field === "favourite" || rule.field === "rated") {
        // "Favourite is yes" is how a form reads it; "is favourite" is how a person says it.
        return `${field} is ${rule.value === true || rule.value === "true" ? "yes" : "no"}`;
    }
    if (rule.value === null || rule.value === "")
        return `${field} ${op} …`;
    return `${field} ${op} ${rule.value}`;
}
/** A one-line summary of a whole tree, for the index table. */
export function describeGroup(group) {
    if (group.children.length === 0)
        return "Everything";
    const parts = group.children.map((child) => child.kind === "rule" ? describeRule(child) : `(${describeGroup(child)})`);
    const joined = parts.join(group.join === "or" ? " or " : " and ");
    return group.negate ? `not (${joined})` : joined;
}
/** Every rule in the tree, depth-first — the order the builder shows them in. */
export function flattenRules(group) {
    return group.children.flatMap((child) => (child.kind === "rule" ? [child] : flattenRules(child)));
}
/**
 * What is wrong with a tree, if anything.
 *
 * Returns problems rather than throwing, because the builder is edited live: a half-typed rule is
 * the normal state of this screen, and the right response is a quiet mark on that row and a count
 * that holds still — not an error page.
 */
export function validate(group, path = []) {
    const problems = [];
    group.children.forEach((child, index) => {
        const here = [...path, index];
        if (child.kind === "group") {
            problems.push(...validate(child, here));
            if (child.children.length === 0)
                problems.push({ path: here, message: "This group has no rules in it." });
            return;
        }
        if (!FIELD_OPS[child.field]?.includes(child.op)) {
            problems.push({ path: here, message: `${FIELD_LABELS[child.field]} cannot be compared that way.` });
        }
        if (child.value === null || child.value === "") {
            problems.push({ path: here, message: "This rule has no value yet." });
        }
        if (NUMERIC_FIELDS.includes(child.field) && child.value !== null && Number.isNaN(Number(child.value))) {
            problems.push({ path: here, message: "That needs to be a number." });
        }
        if (child.field === "plays" && child.value !== null && Number(child.value) < 0) {
            // Never played is zero plays. There is no such thing as fewer.
            problems.push({ path: here, message: "Plays start at 0." });
        }
        if (child.field === "score" && child.value !== null && (Number(child.value) < 1 || Number(child.value) > 10)) {
            problems.push({ path: here, message: "Scores run 1 to 10." });
        }
    });
    return problems;
}
/** True when the tree is safe to run. An empty tree IS runnable — it means everything. */
export function isRunnable(group) {
    return validate(group).length === 0;
}
//# sourceMappingURL=rules.js.map